/**
 * Nav Menu Builder
 * Builds desktop/mobile navigation from Header blocks and link lists.
 *
 * Authoring lives on the Header section:
 *   - Nav Family Member blocks + a Navigation menu (link list)
 *   - Learn menu / Mobile category menu link lists
 *   - Optional Nav Banner and Nav Category blocks
 */

(function() {
  'use strict';

  function readJsonNodes(selector) {
    const items = [];
    document.querySelectorAll(selector).forEach(element => {
      try {
        items.push(JSON.parse(element.textContent));
      } catch (e) {
        console.error('Failed to parse nav JSON for', selector, e);
      }
    });
    return items;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function familyId(family, index) {
    return family.id || ('family-' + index);
  }

  function buildDesktopProductCard(product) {
    const imageHtml = product.image
      ? `<div class="desktop-nav-v3__product-image">
          <img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title || '')}" loading="lazy">
        </div>`
      : `<div class="desktop-nav-v3__product-image">
          <div class="desktop-nav-v3__product-placeholder"></div>
        </div>`;

    const badgeHtml = product.showNewBadge
      ? `<span class="desktop-nav-v3__product-badge">New</span>`
      : '';

    const title = product.shortTitle || product.title || '';

    const descHtml = product.description
      ? `<span class="desktop-nav-v3__product-description">${escapeHtml(product.description)}</span>`
      : '';

    return `
      <a href="${escapeHtml(product.url || '#')}" class="desktop-nav-v3__product-card">
        ${badgeHtml}
        ${imageHtml}
        <div class="desktop-nav-v3__product-content">
          <span class="desktop-nav-v3__product-title">${escapeHtml(title)}</span>
          ${descHtml}
        </div>
      </a>
    `;
  }

  function buildFamilyButton(family, index, isActive) {
    const id = familyId(family, index);
    const imageHtml = family.image
      ? `<span class="desktop-nav-v3__family-btn-icon">
          <img src="${escapeHtml(family.image)}" alt="" width="24" height="24" loading="lazy">
        </span>`
      : '';
    const subtitleHtml = family.subtitle
      ? `<span class="desktop-nav-v3__family-btn-subtitle">${escapeHtml(family.subtitle)}</span>`
      : '';

    return `
      <a
        href="${escapeHtml(family.url || '#')}"
        class="desktop-nav-v3__family-btn${isActive ? ' is-active' : ''}"
        data-nav-family-trigger="${escapeHtml(id)}"
        role="tab"
        aria-selected="${isActive ? 'true' : 'false'}"
        aria-controls="nav-family-panel-${escapeHtml(id)}"
        id="nav-family-tab-${escapeHtml(id)}"
      >
        ${imageHtml}
        <span class="desktop-nav-v3__family-btn-label">
          <span class="desktop-nav-v3__family-btn-title">${escapeHtml(family.title || '')}</span>
          ${subtitleHtml}
        </span>
        <span class="desktop-nav-v3__family-btn-arrow" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M13.6313 17.2154L12.985 16.5452L17.0735 12.4567H5.1543V11.5337H17.0735L12.985 7.44517L13.6313 6.78442L18.8468 11.9999L13.6313 17.2154Z" fill="currentColor"/>
          </svg>
        </span>
      </a>
    `;
  }

  function buildFamilyPanel(family, index, isActive) {
    const id = familyId(family, index);
    const subtitleHtml = family.subtitle
      ? `<span class="desktop-nav-v3__family-panel-subtitle">${escapeHtml(family.subtitle)}</span>`
      : '';
    const productItems = (family.items || [])
      .filter(item => item.type === 'product' || item.url)
      .map(buildDesktopProductCard)
      .join('');

    return `
      <div
        class="desktop-nav-v3__family-panel${isActive ? ' is-active' : ''}"
        data-nav-family-panel="${escapeHtml(id)}"
        id="nav-family-panel-${escapeHtml(id)}"
        role="tabpanel"
        aria-labelledby="nav-family-tab-${escapeHtml(id)}"
      >
        <h3 class="desktop-nav-v3__family-panel-title">
          <span class="desktop-nav-v3__family-panel-name">${escapeHtml(family.title || '')}</span>
          ${subtitleHtml}
        </h3>
        <div class="desktop-nav-v3__product-grid">
          ${productItems}
        </div>
      </div>
    `;
  }

  function buildDesktopShopMega(shopFamilies) {
    if (shopFamilies.length === 0) {
      return '<div class="desktop-nav-v3__empty">No shop categories configured.</div>';
    }

    const defaultIndex = Math.max(0, shopFamilies.findIndex((family) => family.openByDefault));
    const familyButtons = shopFamilies
      .map((family, index) => buildFamilyButton(family, index, index === defaultIndex))
      .join('');
    const familyPanels = shopFamilies
      .map((family, index) => buildFamilyPanel(family, index, index === defaultIndex))
      .join('');

    return `
      <div class="desktop-nav-v3__mega">
        <div class="desktop-nav-v3__mega-families" role="tablist" aria-label="Shop by family member">
          ${familyButtons}
        </div>
        <div class="desktop-nav-v3__mega-products">
          ${familyPanels}
        </div>
      </div>
    `;
  }

  function buildMobileCategoryItem(category) {
    const imageHtml = category.image
      ? `<div class="mobile-nav-v3__category-image">
          <img src="${escapeHtml(category.image)}" alt="${escapeHtml(category.title || '')}" loading="lazy">
        </div>`
      : `<div class="mobile-nav-v3__category-image">
          <div class="mobile-nav-v3__category-placeholder"></div>
        </div>`;

    return `
      <a href="${escapeHtml(category.url || '#')}" class="mobile-nav-v3__category-item">
        ${imageHtml}
        <div class="mobile-nav-v3__category-content">
          <span class="mobile-nav-v3__category-title">${escapeHtml(category.title || '')}</span>
        </div>
        <span class="mobile-nav-v3__arrow">
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
<path d="M13.6313 17.2154L12.985 16.5452L17.0735 12.4567H5.1543V11.5337H17.0735L12.985 7.44517L13.6313 6.78442L18.8468 11.9999L13.6313 17.2154Z" fill="#2C3D50"/>
</svg>
        </span>
      </a>
    `;
  }

  function buildMobileFamilyItem(family) {
    const imageHtml = family.image
      ? `<div class="mobile-nav-v3__family-image">
          <img src="${escapeHtml(family.image)}" alt="${escapeHtml(family.title || '')}" loading="lazy">
        </div>`
      : `<div class="mobile-nav-v3__family-image">
          <div class="mobile-nav-v3__family-placeholder"></div>
        </div>`;

    const subtitleHtml = family.subtitle
      ? `<span class="mobile-nav-v3__family-subtitle">${escapeHtml(family.subtitle)}</span>`
      : '';

    return `
      <a href="${escapeHtml(family.url || '#')}" class="mobile-nav-v3__family-item">
        ${imageHtml}
        <div class="mobile-nav-v3__family-content">
          <span class="mobile-nav-v3__family-title">${escapeHtml(family.title || '')}</span>
          ${subtitleHtml}
        </div>
        <span class="mobile-nav-v3__arrow">
<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
<path d="M13.6313 17.2154L12.985 16.5452L17.0735 12.4567H5.1543V11.5337H17.0735L12.985 7.44517L13.6313 6.78442L18.8468 11.9999L13.6313 17.2154Z" fill="#2C3D50"/>
</svg>
        </span>
      </a>
    `;
  }

  function buildMobileLearnItem(item) {
    return `
      <a href="${escapeHtml(item.url || '#')}" class="mobile-nav-v3__learn-item">
        <div class="mobile-nav-v3__learn-content">
          <span class="mobile-nav-v3__learn-title">${escapeHtml(item.title || '')}</span>
        </div>
        <span class="mobile-nav-v3__arrow">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M13.6313 17.2154L12.985 16.5452L17.0735 12.4567H5.1543V11.5337H17.0735L12.985 7.44517L13.6313 6.78442L18.8468 11.9999L13.6313 17.2154Z" fill="#2C3D50"/>
          </svg>
        </span>
      </a>
    `;
  }

  function buildMobileCategoryList(categories) {
    if (categories.length === 0) return '';

    return `
      <div class="mobile-nav-v3__section">
        <h3 class="mobile-nav-v3__section-title">Shop By Category</h3>
        <div class="mobile-nav-v3__list">
          ${categories.map(buildMobileCategoryItem).join('')}
        </div>
      </div>
    `;
  }

  function buildMobileFamilyList(shopFamilies) {
    if (shopFamilies.length === 0) return '';

    return `
      <div class="mobile-nav-v3__section">
        <h3 class="mobile-nav-v3__section-title">Shop By Family Member</h3>
        <div class="mobile-nav-v3__list">
          ${shopFamilies.map(buildMobileFamilyItem).join('')}
        </div>
      </div>
    `;
  }

  function buildMobileLearnList(learnItems) {
    if (learnItems.length === 0) return '';

    return `
      <div class="mobile-nav-v3__section">
        <div class="mobile-nav-v3__accordion-header" data-learn-accordion-toggle>
          <span class="mobile-nav-v3__section-title">Learn</span>
          <span class="mobile-nav-v3__accordion-chevron">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M7.41 8.59L12 13.17L16.59 8.59L18 10L12 16L6 10L7.41 8.59Z" fill="#2C3D50"/>
            </svg>
          </span>
        </div>
        <div class="mobile-nav-v3__list mobile-nav-v3__accordion-content" data-learn-accordion-content style="display: none;">
          ${learnItems.map(buildMobileLearnItem).join('')}
        </div>
      </div>
    `;
  }

  function injectDesktopNav(shopFamilies, learnItems) {
    const shopDropdownContainer = document.querySelector('[data-nav-shop-dropdown]');
    if (shopDropdownContainer && !shopDropdownContainer.querySelector('.desktop-nav-v3__mega')) {
      shopDropdownContainer.innerHTML = buildDesktopShopMega(shopFamilies);
    }

    const learnDropdownContainer = document.querySelector('[data-nav-learn-dropdown]');
    if (learnDropdownContainer && !learnDropdownContainer.querySelector('.desktop-nav-v3__learn-link')) {
      const linksHtml = learnItems.map(item =>
        `<a href="${escapeHtml(item.url || '#')}" class="desktop-nav-v3__learn-link">${escapeHtml(item.title || '')}</a>`
      ).join('');
      learnDropdownContainer.innerHTML = linksHtml || '<span class="desktop-nav-v3__empty">No learn items configured.</span>';
    }
  }

  function fillFromTemplate(selector, templateId) {
    const template = document.getElementById(templateId);
    if (!template) return false;
    const html = template.innerHTML.trim();
    if (!html) return false;
    document.querySelectorAll(selector).forEach((container) => {
      container.innerHTML = html;
    });
    return true;
  }

  function injectMobileNav(shopFamilies, shopCategories, learnItems) {
    if (!fillFromTemplate('[data-mobile-nav-shop]', 'MobileNavShopSource')) {
      document.querySelectorAll('[data-mobile-nav-shop]').forEach(container => {
        if (container.querySelector('.mobile-nav-v3__section')) return;
        container.innerHTML = buildMobileCategoryList(shopCategories) + buildMobileFamilyList(shopFamilies);
      });
    }

    if (!fillFromTemplate('[data-mobile-nav-learn]', 'MobileNavLearnSource')) {
      document.querySelectorAll('[data-mobile-nav-learn]').forEach(container => {
        if (container.querySelector('.mobile-nav-v3__section')) return;
        container.innerHTML = buildMobileLearnList(learnItems);
      });
    }
  }

  function initFamilyPanels() {
    const shopDropdown = document.querySelector('.desktop-nav-v3__dropdown[data-dropdown="shop"]');
    if (!shopDropdown || shopDropdown.dataset.familyPanelsBound) return;
    if (!shopDropdown.querySelector('[data-nav-family-trigger]')) return;

    shopDropdown.dataset.familyPanelsBound = 'true';

    const activate = (familyId) => {
      shopDropdown.querySelectorAll('[data-nav-family-trigger]').forEach((btn) => {
        const on = btn.getAttribute('data-nav-family-trigger') === familyId;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-selected', on ? 'true' : 'false');
      });

      shopDropdown.querySelectorAll('[data-nav-family-panel]').forEach((panel) => {
        const on = panel.getAttribute('data-nav-family-panel') === familyId;
        panel.classList.toggle('is-active', on);
      });
    };

    shopDropdown.addEventListener('mouseover', (event) => {
      const trigger = event.target.closest('[data-nav-family-trigger]');
      if (!trigger || !shopDropdown.contains(trigger)) return;
      activate(trigger.getAttribute('data-nav-family-trigger'));
    }, true);

    shopDropdown.addEventListener('focusin', (event) => {
      const trigger = event.target.closest('[data-nav-family-trigger]');
      if (!trigger) return;
      activate(trigger.getAttribute('data-nav-family-trigger'));
    });
  }

  function initLearnAccordion() {
    document.querySelectorAll('[data-learn-accordion-toggle]').forEach(header => {
      if (header.dataset.navAccordionBound) return;
      header.dataset.navAccordionBound = 'true';
      header.addEventListener('click', () => {
        const content = header.nextElementSibling;
        const chevron = header.querySelector('.mobile-nav-v3__accordion-chevron');
        const isOpen = content.style.display !== 'none';

        content.style.display = isOpen ? 'none' : 'block';
        chevron.classList.toggle('mobile-nav-v3__accordion-chevron--open', !isOpen);
      });
    });
  }

  function init() {
    const families = readJsonNodes('[data-nav-family]');
    const shopCategories = readJsonNodes('[data-nav-shop-category]');
    const learnItems = readJsonNodes('[data-nav-learn-item]');
    const shopFamilies = families.filter(fam => fam.type !== 'learn');

    injectDesktopNav(shopFamilies, learnItems);
    injectMobileNav(shopFamilies, shopCategories, learnItems);
    initFamilyPanels();
    initLearnAccordion();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', init);
  document.addEventListener('shopify:section:reorder', init);
})();
